import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, X, Check, ChevronDown } from 'lucide-react';

/**
 * SearchSelect — liste déroulante avec recherche typée (combobox).
 *
 * Props:
 *  - options:        Array<{ value: string, label: string, sublabel?: string, keywords?: string }>
 *  - value:          string  (option.value sélectionnée)
 *  - onChange:       (value: string) => void
 *  - placeholder:    string (texte par défaut)
 *  - emptyText:      string (texte affiché si aucune option ne correspond)
 *  - testId:         string (data-testid sur le bouton trigger)
 *  - disabled:       boolean
 *  - autoFocus:      boolean — focus auto sur la search à l'ouverture
 */
const SearchSelect = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'Sélectionner…',
  emptyText = 'Aucun résultat',
  testId,
  disabled = false,
  autoFocus = true,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selectedOption = useMemo(
    () => options.find((o) => o.value === value),
    [options, value]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((o) => {
      const haystack = `${o.label} ${o.sublabel || ''} ${o.keywords || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [options, search]);

  // Fermer si clic à l'extérieur
  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Focus auto à l'ouverture
  useEffect(() => {
    if (open && autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
    if (!open) {
      setSearch('');
      setActiveIndex(-1);
    }
  }, [open, autoFocus]);

  // Scroll l'option active en vue
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${activeIndex}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleSelect = (val) => {
    onChange?.(val);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && filtered[activeIndex]) {
        handleSelect(filtered[activeIndex].value);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className={`search-select ${open ? 'is-open' : ''}`} ref={wrapperRef} data-testid={testId}>
      <button
        type="button"
        className={`search-select-trigger ${disabled ? 'is-disabled' : ''}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`search-select-value ${!selectedOption ? 'is-placeholder' : ''}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown size={16} className="search-select-chevron" />
      </button>

      {open && (
        <div className="search-select-dropdown" role="listbox">
          <div className="search-select-search">
            <Search size={14} className="search-select-search-icon" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Rechercher…"
              className="search-select-search-input"
              data-testid={testId ? `${testId}-search` : undefined}
            />
            {search && (
              <button
                type="button"
                className="search-select-search-clear"
                onClick={() => setSearch('')}
                aria-label="Effacer"
              >
                <X size={12} />
              </button>
            )}
          </div>

          <ul ref={listRef} className="search-select-list">
            {filtered.length === 0 && (
              <li className="search-select-empty">{emptyText}</li>
            )}
            {filtered.map((opt, idx) => {
              const isSelected = opt.value === value;
              const isActive = idx === activeIndex;
              return (
                <li
                  key={opt.value}
                  data-idx={idx}
                  className={`search-select-option ${isSelected ? 'is-selected' : ''} ${isActive ? 'is-active' : ''}`}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => handleSelect(opt.value)}
                  role="option"
                  aria-selected={isSelected}
                >
                  <div className="search-select-option-text">
                    <span className="search-select-option-label">{opt.label}</span>
                    {opt.sublabel && (
                      <span className="search-select-option-sublabel">{opt.sublabel}</span>
                    )}
                  </div>
                  {isSelected && <Check size={14} className="search-select-option-check" />}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchSelect;
